import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { ArrowCircleLeftOutlined as ArrowCircleLeftOutlinedIcon } from "@mui/icons-material";

import {
  setCategorySelected,
  setSearchTerm,
} from "../../../services/store/storeSlice";

import ProductCard from "../../../components/store/ProductCard";
import ProductLimit from "../../../components/store/action/ProductLimit";
import ProductSort from "../../../components/store/action/ProductSort";
import PaginationRounded from "../../../components/store/action/PaginationRounded";
import SideBarStoreTop from "./SideBarStoreTop";
import Translation from "../../../utils/Translation";
import { checkScreenLaptop, getValByLang } from "../../../utils/helpers";

export default function ProductStore() {
  const dispatch = useDispatch();

  const {
    isLoading,
    productCount,
    products,
    categoriesAvailable,
    CategorySelected,
    search_term,
    sortBy,
    limit,
    page,
    attributesAvailable,
  } = useSelector((state) => state.store);

  const { language, screenWidth, mediumWidthScreen } = useSelector(
    (state) => state.global
  );

  const handleClear = () => {
    dispatch(setCategorySelected(0));
    dispatch(setSearchTerm(""));
  };

  const isFiltered = !!search_term || !!CategorySelected;

  return (
    <>
      {/* EMPTY STATE */}
      {!isLoading && products.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          minHeight="300px"
          py={4}
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          <img
            src="/images/empty-products.svg"
            alt="No products found"
            style={{ width: "160px", marginBottom: "24px", opacity: 0.7 }}
          />
          <Typography variant="h5" gutterBottom>
            <Translation
              data="store.no_products_found_title"
              defaultText="No products found"
            />
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 400, color: "#666" }}
            gutterBottom
          >
            <Translation
              data="store.no_products_found_description"
              defaultText="We couldn’t find any products matching your filters."
            />
          </Typography>
          {isFiltered && (
            <Button
              variant="outlined"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleClear}
              startIcon={<ArrowCircleLeftOutlinedIcon />}
            >
              <Translation
                data="store.back_to_home"
                defaultText="Clear filters"
              />
            </Button>
          )}
        </Box>
      )}

      {/* FILTER BAR + TOP CONTROLS */}
      {products.length > 0 && (
        <Container className="no-padding page_size">
          {!checkScreenLaptop(screenWidth, mediumWidthScreen) && (
            <Box className="mb-2">
              <SideBarStoreTop
                isLoading={isLoading}
                attributesAvailable={attributesAvailable}
                categoriesAvailable={categoriesAvailable}
               
              />
            </Box>
          )}

          <Grid container  mb={3} spacing={2}>
            <Grid item xs={12} md={6} lg={8}>
              {isFiltered && (
                <Typography
                  variant="b"
                  component="h3"
                  className="search-result"
                  sx={{ mt: 2 }}
                >
                  <span className="no-bold">
                    {productCount}{" "}
                    <Translation data="store.results_for" />
                  </span>{" "}
                  "
                  {categoriesAvailable?.name
                    ? getValByLang(categoriesAvailable.name, language)
                    : search_term}
                  "
                </Typography>
              )}
            </Grid>
            <Grid item xs={6} md={3} lg={2}>
              <ProductSort sortBy={sortBy} />
            </Grid>
            <Grid item xs={6} md={3} lg={2}>
              <ProductLimit limit={limit} />
            </Grid>
          </Grid>
        </Container>
      )}

      {/* PRODUCT LIST */}
      {products.length > 0 && (
        <Container  className="no-padding page_size products">
          <Grid container   className="products-grid">
            {isLoading ? (
              <Grid item xs={12}>
                <CircularProgress className="product-preloader-position" />
              </Grid>
            ) : (
              products.map((val, idx) => (
                <Grid key={idx} item xs={6} sm={6} md={4} lg={4} xl={3}  >
                  <ProductCard product={val} sx={{ p: 2 }} />
                </Grid>
              ))
            )}
          </Grid>
        </Container>
      )}

      {/* PAGINATION */}
      {!isLoading && products.length > 0 && (
        <Container className="no-padding page_size">
          <PaginationRounded
            limit={limit}
            productCount={productCount}
            page={page}
          />
        </Container>
      )}
    </>
  );
}
